import React, { useState, useEffect } from "react";
import { formatCurrency, formatPercentage } from "../../../../utils/formatters";
import BaseViewChart from "./BaseViewChart";

import logger from '../../../../utils/logger';

const ResultsDisplay = ({ results, formData }) => {
  const [activeTab, setActiveTab] = useState("base"); // Changed from 'summary' to 'base'
  const scenarioData = results.baseScenario;

  // Add state for the data
  const [rmdTables, setRmdTables] = useState(null);
  const [taxData, setTaxData] = useState(null);

  // Add useEffect to fetch data
  useEffect(() => {
    Promise.all([
      fetch("/data/rmdTables.json").then((res) => res.json()),
      fetch("/data/taxData.json").then((res) => res.json()),
    ])
      .then(([rmdData, taxData]) => {
        setRmdTables(rmdData);
        setTaxData(taxData);
      })
      .catch((error) => {
        logger.error("Error loading data:", error);
      });
  }, []);

  // Move calculateAge inside the component
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 0;

    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred this year
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  // Use the yearsToProject from the scenario data
  const yearsToProject = scenarioData.yearsToProject;
  const currentYear = new Date().getFullYear();

  logger.info("ResultsDisplay received:", {
    scenarioData,
    yearsToProject,
    currentYear,
  });

  const getTaxBrackets = (year, filingStatus) => {
    // Convert filing status to match taxData.js format
    const statusMap = {
      single: "single",
      married_joint: "married",
      married_separate: "marriedseparate",
      head_household: "head",
    };
    const status = statusMap[filingStatus] || "single";

    // Get brackets for the year if they exist
    const yearBrackets = taxData ? taxData.TAX_BRACKETS[year] : null;
    if (!yearBrackets) {
      // If no data for this year, adjust the latest year's brackets by COLA
      const latestYear = Math.max(
        ...Object.keys(taxData ? taxData.TAX_BRACKETS : {}).map(Number),
      );
      const latestBrackets = taxData ? taxData.TAX_BRACKETS[latestYear] : null;
      const yearsSinceLatest = year - latestYear;
      const colaFactor = Math.pow(
        1 + (scenarioData?.inflationRate || 0.025) / 100,
        yearsSinceLatest,
      );

      // Create adjusted brackets
      return latestBrackets.map((bracket) => {
        const rate = parseFloat(bracket.rate) / 100;
        const range = bracket[status].split(" to ");
        const min = parseFloat(range[0].replace(/[$,]/g, "")) * colaFactor;
        const max = range[1]
          ? parseFloat(range[1].replace(/[$,]/g, "")) * colaFactor
          : Infinity;

        return {
          threshold: min,
          rate: rate,
          maxThreshold: max === Infinity ? null : max,
        };
      });
    }

    // Convert tax data format to our calculation format
    return yearBrackets.map((bracket) => {
      const rate = parseFloat(bracket.rate) / 100;
      const range = bracket[status].split(" to ");
      const min = parseFloat(range[0].replace(/[$,]/g, ""));
      const max = range[1]
        ? parseFloat(range[1].replace(/[$,]/g, ""))
        : Infinity;

      return {
        threshold: min,
        rate: rate,
        maxThreshold: max === Infinity ? null : max,
      };
    });
  };

  const getStandardDeduction = (year, filingStatus) => {
    // Add debugging
    logger.info("Getting standard deduction for:", {
      year,
      filingStatus,
      deductions: taxData ? taxData.DEDUCTIONS : null,
    });

    // Convert filing status to match taxData.js format
    const statusMap = {
      single: "Single",
      married_joint: "Married Filing Jointly",
      married_separate: "Married Filing Separately",
      head_household: "Head of Household",
    };
    const status = statusMap[filingStatus] || "Single";

    // Get deductions for the year if they exist
    const yearDeductions = taxData ? taxData.DEDUCTIONS[year] : null;
    if (!yearDeductions) {
      // If no data for this year, adjust the latest year's deduction by COLA
      const latestYear = Math.max(
        ...Object.keys(taxData ? taxData.DEDUCTIONS : {}).map(Number),
      );
      const latestDeductions = taxData ? taxData.DEDUCTIONS[latestYear] : null;

      // Add defensive check
      if (!latestDeductions) {
        logger.error("No deduction data found for any year");
        return 12950; // Return 2022 standard deduction as fallback
      }

      const latestDeduction = latestDeductions.find((d) => d.status === status);
      if (!latestDeduction) {
        logger.error("No deduction found for status:", status);
        return 12950; // Return 2022 standard deduction as fallback
      }

      const yearsSinceLatest = year - latestYear;
      const colaFactor = Math.pow(
        1 + (scenarioData?.inflationRate || 0.025) / 100,
        yearsSinceLatest,
      );

      return (
        parseFloat(latestDeduction.standard.replace(/[$,]/g, "")) * colaFactor
      );
    }

    const deduction = yearDeductions.find((d) => d.status === status);
    if (!deduction) {
      logger.error("No deduction found for status:", status, "in year:", year);
      return 12950; // Return 2022 standard deduction as fallback
    }

    return parseFloat(deduction.standard.replace(/[$,]/g, ""));
  };

  const calculateTaxOwed = (taxableIncome, filingStatus, year) => {
    const brackets = getTaxBrackets(year, filingStatus);
    let tax = 0;
    let remainingIncome = taxableIncome;

    for (let i = 0; i < brackets.length; i++) {
      const currentBracket = brackets[i];
      const nextBracket = brackets[i + 1];

      const bracketIncome = nextBracket
        ? Math.min(
            remainingIncome,
            nextBracket.threshold - currentBracket.threshold,
          )
        : remainingIncome;

      if (bracketIncome <= 0) break;

      tax += bracketIncome * currentBracket.rate;
      remainingIncome -= bracketIncome;
    }

    return tax;
  };

  const calculateLiabilityBalances = (debts) => {
    const liabilityProjections = [];

    for (let i = 0; i < yearsToProject; i++) {
      const yearlyBalances = debts.map((debt) => {
        const rate = parseFloat(debt.interestRate) / 100;
        const payment =
          parseFloat(debt.actualPayment) || parseFloat(debt.minimumPayment);
        let balance = parseFloat(debt.balance);

        // Calculate remaining balance after i years of payments
        for (let year = 0; year < i; year++) {
          const yearlyInterest = balance * rate;
          const principalPayment = payment * 12 - yearlyInterest;
          balance = Math.max(0, balance - principalPayment);
        }

        return {
          name: debt.name,
          balance: balance,
        };
      });

      liabilityProjections.push(yearlyBalances);
    }

    return liabilityProjections;
  };

  const calculateNetWorth = (assetBalances, liabilityBalances) => {
    const TAX_RATE_ON_DEFERRED = 0.12; // 12% tax on tax-deferred assets

    return assetBalances.map((assets, index) => {
      // Calculate tax liability on tax-deferred assets
      const taxDeferredTaxLiability = assets.taxDeferred * TAX_RATE_ON_DEFERRED;

      // Calculate total assets after tax on deferred
      const totalAssets =
        assets.nonQualified +
        assets.taxDeferred * (1 - TAX_RATE_ON_DEFERRED) + // Apply tax to deferred
        assets.roth;

      // Calculate total liabilities
      const totalLiabilities = liabilityBalances[index].reduce(
        (sum, liability) => sum + liability.balance,
        0,
      );

      return totalAssets - totalLiabilities;
    });
  };

  // Helper function for possessive form
  const getPossessiveForm = (name) => {
    if (!name) return "";
    return name.endsWith("s") ? `${name}'` : `${name}'s`;
  };

  // Add a helper function to format the income type display
  const formatIncomeType = (type) => {
    if (!type) return "";
    // Replace underscores with spaces and capitalize each word
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const calculateRetirementDistributions = (
    householdTotals,
    additionalDistributions = [],
    view = "base",
  ) => {
    const distributions = [];
    // Set withholding rate based on view
    const WITHHOLDING_RATE = view === "ira" ? 0.12 : 0.2; // 12% for IRA view, 20% for others
    const currentYear = new Date().getFullYear();

    // Track remaining balances
    let remainingBalances = {
      taxDeferred: scenarioData.assetsByType.tax_deferred,
      nonQualified: scenarioData.assetsByType.non_qualified,
      roth: scenarioData.assetsByType.roth,
    };

    for (let i = 0; i < yearsToProject; i++) {
      const year = currentYear + i;

      // Calculate RMD for this year
      const firstMemberAge =
        calculateAge(formData.household.firstMember.dateOfBirth) + i;
      const rmdDivisor = rmdTables?.UNIFORM_LIFETIME_TABLE[firstMemberAge] || 0;
      const rmdAmount = rmdDivisor
        ? remainingBalances.taxDeferred / rmdDivisor
        : 0;

      // Calculate static deficit (this is what we need net)
      const staticDeficit = householdTotals[i].goal - householdTotals[i].net;

      // Calculate gross needed to achieve net after withholding
      const grossNeeded =
        staticDeficit > 0 ? staticDeficit / (1 - WITHHOLDING_RATE) : 0;

      // Take the larger of RMD or needed gross
      let grossDistribution = Math.max(rmdAmount, grossNeeded);
      let taxWithholding = 0;
      let netDistribution = 0;

      // Track which accounts we're pulling from
      let distributionSources = {
        taxDeferred: 0,
        nonQualified: 0,
        roth: 0,
      };

      // First satisfy RMD from tax-deferred if applicable
      if (rmdAmount > 0) {
        const rmdTaken = Math.min(rmdAmount, remainingBalances.taxDeferred);
        distributionSources.taxDeferred = rmdTaken;
        remainingBalances.taxDeferred -= rmdTaken;
        taxWithholding += rmdTaken * WITHHOLDING_RATE;
      }

      // If we still need more after RMD, pull from accounts in order
      let remainingNeeded = grossDistribution - distributionSources.taxDeferred;

      if (remainingNeeded > 0) {
        // First try tax-deferred (beyond RMD)
        const additionalTaxDeferred = Math.min(
          remainingNeeded,
          remainingBalances.taxDeferred,
        );
        if (additionalTaxDeferred > 0) {
          distributionSources.taxDeferred += additionalTaxDeferred;
          remainingBalances.taxDeferred -= additionalTaxDeferred;
          taxWithholding += additionalTaxDeferred * WITHHOLDING_RATE;
          remainingNeeded -= additionalTaxDeferred;
        }

        // Then try non-qualified
        if (remainingNeeded > 0) {
          const fromNonQualified = Math.min(
            remainingNeeded,
            remainingBalances.nonQualified,
          );
          if (fromNonQualified > 0) {
            distributionSources.nonQualified = fromNonQualified;
            remainingBalances.nonQualified -= fromNonQualified;
            remainingNeeded -= fromNonQualified;
          }
        }

        // Finally, use Roth if needed
        if (remainingNeeded > 0) {
          const fromRoth = Math.min(remainingNeeded, remainingBalances.roth);
          if (fromRoth > 0) {
            distributionSources.roth = fromRoth;
            remainingBalances.roth -= fromRoth;
            remainingNeeded -= fromRoth;
          }
        }
      }

      // Calculate total gross and net distributions
      grossDistribution = Object.values(distributionSources).reduce(
        (sum, val) => sum + val,
        0,
      );
      netDistribution = grossDistribution - taxWithholding;

      distributions.push({
        gross: grossDistribution,
        taxWithholding: taxWithholding,
        net: netDistribution,
        rmdAmount: rmdAmount,
        sources: distributionSources,
        remainingBalances: { ...remainingBalances },
      });
    }

    return distributions;
  };

  const calculateTaxTotals = (householdTotals, retirementDistributions) => {
    const taxTotals = [];
    const currentYear = new Date().getFullYear();

    for (let i = 0; i < yearsToProject; i++) {
      const year = currentYear + i;
      // Calculate total gross income (including retirement distributions)
      const totalGrossIncome =
        householdTotals[i].gross + retirementDistributions[i].gross;
      const standardDeduction = getStandardDeduction(
        year,
        formData.filingStatus,
      );

      // Get federal tax withholding from income sources
      const incomeTaxesWithheld = householdTotals[i].federalTaxWithholding;
      const retirementTaxesWithheld = retirementDistributions[i].taxWithholding;

      // Total taxes withheld is the sum of both
      const totalTaxesWithheld = incomeTaxesWithheld + retirementTaxesWithheld;

      // Calculate taxable income and tax owed
      const taxableIncome = Math.max(
        0,
        totalGrossIncome -
          standardDeduction -
          householdTotals[i].otherDeductions,
      );
      const taxOwed = calculateTaxOwed(
        taxableIncome,
        formData.filingStatus,
        year,
      );

      taxTotals.push({
        totalGrossIncome,
        taxableIncome,
        taxesWithheld: totalTaxesWithheld,
        otherDeductions: householdTotals[i].otherDeductions,
        taxOwed,
        taxDifference: totalTaxesWithheld - taxOwed,
      });
    }
    return taxTotals;
  };

  const calculateAssetBalances = (
    householdTotals,
    retirementDistributions,
    taxTotals,
  ) => {
    // Initialize starting balances
    let currentBalances = {
      nonQualified: parseFloat(
        results.baseScenario.assetsByType["non_qualified"] || 0,
      ),
      taxDeferred: parseFloat(
        results.baseScenario.assetsByType["tax_deferred"] || 0,
      ),
      roth: parseFloat(results.baseScenario.assetsByType["roth"] || 0),
    };

    const balances = [];

    for (let i = 0; i < yearsToProject; i++) {
      // Apply investment returns first
      const returnRate = (scenarioData?.investmentReturn || 6.4) / 100;
      currentBalances = {
        nonQualified: currentBalances.nonQualified * (1 + returnRate),
        taxDeferred: currentBalances.taxDeferred * (1 + returnRate),
        roth: currentBalances.roth * (1 + returnRate),
      };

      // Handle Roth conversions for Roth view
      if (activeTab === "roth") {
        const year = currentYear + i;
        const brackets = getTaxBrackets(year, formData.filingStatus);

        // Find the 12% bracket more reliably
        const twelvePctBracket = brackets.find(
          (b) => Math.abs(b.rate - 0.12) < 0.001,
        );

        if (twelvePctBracket) {
          // Calculate available space in 12% bracket
          const taxableIncome = taxTotals[i].taxableIncome;
          const bracketStart = twelvePctBracket.threshold;
          const bracketEnd = twelvePctBracket.maxThreshold;

          logger.info(`Year ${year} Roth Conversion Analysis:`, {
            taxableIncome,
            bracketStart,
            bracketEnd,
            currentTaxDeferred: currentBalances.taxDeferred,
            currentNonQualified: currentBalances.nonQualified,
          });

          // Calculate space available in 12% bracket
          const spaceIn12PctBracket =
            bracketEnd - Math.max(taxableIncome, bracketStart);

          if (spaceIn12PctBracket > 0 && currentBalances.taxDeferred > 0) {
            // Calculate conversion amount (limited by available tax-deferred balance)
            const conversionAmount = Math.min(
              spaceIn12PctBracket,
              currentBalances.taxDeferred,
            );
            const taxDue = conversionAmount * 0.12; // 12% tax on conversion

            logger.info(`Potential Roth Conversion:`, {
              spaceIn12PctBracket,
              conversionAmount,
              taxDue,
            });

            // Check if we have enough non-qualified assets to pay the tax
            if (currentBalances.nonQualified >= taxDue) {
              // Perform conversion
              currentBalances.taxDeferred -= conversionAmount;
              currentBalances.roth += conversionAmount;
              currentBalances.nonQualified -= taxDue;

              logger.info(`Executed Roth Conversion:`, {
                convertedAmount: conversionAmount,
                taxPaid: taxDue,
                newTaxDeferred: currentBalances.taxDeferred,
                newRoth: currentBalances.roth,
                newNonQualified: currentBalances.nonQualified,
              });
            }
          }
        }
      }

      // Calculate total deficit/surplus for this year
      const totalDeficitSurplus =
        householdTotals[i].net +
        retirementDistributions[i].net -
        householdTotals[i].goal;

      // Get tax difference from previous year (if exists)
      const prevYearTaxDiff =
        i > 0 ? taxTotals[i - 1].taxesWithheld - taxTotals[i - 1].taxOwed : 0;

      // Add surplus to non-qualified or subtract deficit
      if (totalDeficitSurplus > 0) {
        currentBalances.nonQualified += totalDeficitSurplus;
      } else if (totalDeficitSurplus < 0) {
        const deficit = Math.abs(totalDeficitSurplus);
        if (currentBalances.nonQualified >= deficit) {
          currentBalances.nonQualified -= deficit;
        } else {
          currentBalances.nonQualified = 0;
        }
      }

      // Handle previous year's tax difference
      if (prevYearTaxDiff !== 0) {
        if (prevYearTaxDiff > 0) {
          currentBalances.nonQualified += prevYearTaxDiff;
        } else {
          const taxOwed = Math.abs(prevYearTaxDiff);
          if (currentBalances.nonQualified >= taxOwed) {
            currentBalances.nonQualified -= taxOwed;
          } else {
            currentBalances.nonQualified = 0;
          }
        }
      }

      // Apply retirement distributions (RMDs and other distributions)
      if (retirementDistributions[i].gross > 0) {
        if (currentBalances.taxDeferred >= retirementDistributions[i].gross) {
          currentBalances.taxDeferred -= retirementDistributions[i].gross;
        }
      }

      balances.push({ ...currentBalances });
    }

    return {
      assetBalances: balances,
      updatedAdditionalDistributions: [],
    };
  };

  const calculateIncomeSourceProjection = (
    source,
    memberBirthDate,
    memberLifeExpectancy,
  ) => {
    const projections = [];
    const currentYear = new Date().getFullYear();

    // Parse dates correctly
    const sourceStartDate = source.startDate
      ? new Date(source.startDate)
      : new Date();
    const sourceEndDate = source.endDate ? new Date(source.endDate) : null;

    for (let i = 0; i < yearsToProject; i++) {
      const year = currentYear + i;

      // Calculate inflation factor for this year - FIX THE FORMULA
      const inflationRate = (scenarioData?.inflationRate || 2.5) / 100; // Convert percentage to decimal
      const inflationFactor = Math.pow(1 + inflationRate, i);

      // Calculate active days in this year
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31);

      // Calculate actual start and end dates for this year
      const periodStart =
        sourceStartDate > yearStart ? sourceStartDate : yearStart;
      const periodEnd =
        sourceEndDate && sourceEndDate < yearEnd ? sourceEndDate : yearEnd;

      // Calculate active ratio based on days
      let activeRatio = 0;
      if (periodStart <= periodEnd) {
        // Set both dates to midnight UTC to avoid timezone issues
        const dayStart = new Date(
          Date.UTC(
            periodStart.getFullYear(),
            periodStart.getMonth(),
            periodStart.getDate(),
          ),
        );
        const dayEnd = new Date(
          Date.UTC(
            periodEnd.getFullYear(),
            periodEnd.getMonth(),
            periodEnd.getDate(),
          ),
        );

        const millisecondsPerDay = 1000 * 60 * 60 * 24;
        const totalDays =
          Math.floor((dayEnd - dayStart) / millisecondsPerDay) + 1;

        activeRatio = totalDays / 365;
      }

      // Calculate amounts WITH corrected inflation
      const monthlyGross = parseFloat(source.grossAmount) || 0;
      const annualGross = monthlyGross * 12 * inflationFactor;
      const proratedGross = annualGross * activeRatio;

      // Calculate deductions WITH corrected inflation
      let proratedDeductions = 0;
      let federalTaxWithholding = 0;
      let otherDeductions = 0;

      (source.deductions || []).forEach((deduction) => {
        // Check if deduction is active in this year
        const deductionStart = deduction.startDate
          ? new Date(deduction.startDate)
          : sourceStartDate;
        const deductionEnd = deduction.endDate
          ? new Date(deduction.endDate)
          : deduction.isLifetime
            ? null
            : sourceEndDate;

        // Calculate deduction period for this year
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);

        const deductionPeriodStart =
          deductionStart > yearStart ? deductionStart : yearStart;
        const deductionPeriodEnd =
          deductionEnd && deductionEnd < yearEnd ? deductionEnd : yearEnd;

        // Calculate deduction active ratio
        let deductionActiveRatio = 0;
        if (deductionPeriodStart <= deductionPeriodEnd) {
          const dayStart = new Date(
            Date.UTC(
              deductionPeriodStart.getFullYear(),
              deductionPeriodStart.getMonth(),
              deductionPeriodStart.getDate(),
            ),
          );
          const dayEnd = new Date(
            Date.UTC(
              deductionPeriodEnd.getFullYear(),
              deductionPeriodEnd.getMonth(),
              deductionPeriodEnd.getDate(),
            ),
          );

          const millisecondsPerDay = 1000 * 60 * 60 * 24;
          const totalDays =
            Math.floor((dayEnd - dayStart) / millisecondsPerDay) + 1;

          deductionActiveRatio = totalDays / 365;
        }

        const monthlyAmount = parseFloat(deduction.amount) || 0;
        const annualDeduction = monthlyAmount * 12 * inflationFactor;
        const proratedDeduction = annualDeduction * deductionActiveRatio;

        if (deduction.type === "federal_tax") {
          federalTaxWithholding += proratedDeduction;
        } else {
          otherDeductions += proratedDeduction;
        }
        proratedDeductions += proratedDeduction;
      });

      projections.push({
        year,
        gross: proratedGross,
        deductions: proratedDeductions,
        federalTaxWithholding: federalTaxWithholding,
        otherDeductions: otherDeductions,
        net: proratedGross - proratedDeductions,
      });
    }

    return projections;
  };

  const calculateHouseholdTotals = () => {
    const householdTotals = [];
    const currentYear = new Date().getFullYear();

    for (let i = 0; i < yearsToProject; i++) {
      const year = currentYear + i;
      const inflationFactor = Math.pow(
        1 + (scenarioData?.inflationRate || 0.025) / 100,
        i,
      );

      // Get base monthly goal and apply inflation
      const baseMonthlyGoal =
        parseFloat(formData.assumptions?.monthlyNetIncomeGoal) || 7500;
      let annualGoal = baseMonthlyGoal * 12 * inflationFactor;

      // Add additional income goals for this year
      if (formData.assumptions?.additionalIncomeGoals) {
        formData.assumptions.additionalIncomeGoals.forEach((goal) => {
          const goalStart = goal.startDate ? new Date(goal.startDate) : null;
          const goalEnd = goal.endDate ? new Date(goal.endDate) : null;
          const yearStart = new Date(year, 0, 1);
          const yearEnd = new Date(year, 11, 31);

          // Check if goal is active in this year
          if (
            (!goalStart || yearEnd >= goalStart) &&
            (!goalEnd || yearStart <= goalEnd)
          ) {
            // Calculate active ratio for partial years
            let activeRatio = 1;
            if (goalStart && goalStart.getFullYear() === year) {
              const startDate = new Date(
                Date.UTC(
                  goalStart.getFullYear(),
                  goalStart.getMonth(),
                  goalStart.getDate(),
                ),
              );
              const endDate = new Date(
                Date.UTC(
                  yearEnd.getFullYear(),
                  yearEnd.getMonth(),
                  yearEnd.getDate(),
                ),
              );
              const totalDays =
                Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
              activeRatio = totalDays / 365;
            }
            if (goalEnd && goalEnd.getFullYear() === year) {
              const startDate = new Date(
                Date.UTC(
                  yearStart.getFullYear(),
                  yearStart.getMonth(),
                  yearStart.getDate(),
                ),
              );
              const endDate = new Date(
                Date.UTC(
                  goalEnd.getFullYear(),
                  goalEnd.getMonth(),
                  goalEnd.getDate(),
                ),
              );
              const totalDays =
                Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
              activeRatio = Math.min(activeRatio, totalDays / 365);
            }

            // Add prorated and inflated goal amount
            const monthlyAmount = parseFloat(goal.amount) || 0;
            const annualAmount =
              monthlyAmount * 12 * inflationFactor * activeRatio;
            annualGoal += annualAmount;
          }
        });
      }

      const yearTotal = {
        year,
        gross: 0,
        net: 0,
        federalTaxWithholding: 0,
        otherDeductions: 0,
        expenses: 0,
        goal: annualGoal,
      };

      // Add income sources for this year
      formData.household.firstMember.incomeSources.forEach((source) => {
        const sourceProjection = calculateIncomeSourceProjection(
          source,
          formData.household.firstMember.dateOfBirth,
          formData.assumptions.lifeExpectancy.firstMember,
        )[i];

        yearTotal.gross += sourceProjection.gross;
        yearTotal.federalTaxWithholding +=
          sourceProjection.federalTaxWithholding;
        yearTotal.otherDeductions += sourceProjection.otherDeductions;
        yearTotal.net =
          yearTotal.gross -
          yearTotal.federalTaxWithholding -
          yearTotal.otherDeductions;
      });

      householdTotals.push(yearTotal);
    }

    return householdTotals;
  };

  const renderSummaryTab = () => (
    <div className="space-y-6">
      {/* Scenario Summaries */}
      {Object.entries(results).map(([scenario, data]) => {
        // Skip if data is null
        if (!data) return null;

        return (
          <div key={scenario} className="border rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 capitalize">
              {scenario.replace(/([A-Z])/g, " $1").trim()}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Total Retirement Income
                </div>
                <div className="text-lg font-medium">
                  {formatCurrency(data.totalRetirementIncome || 0)}
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Taxes Paid</div>
                <div className="text-lg font-medium">
                  {formatCurrency(data.totalTaxesPaid || 0)}
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-sm text-gray-600 dark:text-gray-400">Ending Balance</div>
                <div className="text-lg font-medium">
                  {formatCurrency(data.endingBalance || 0)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderDetailsTab = (scenarioData) => {
    // Calculate all the values needed
    const householdTotals = calculateHouseholdTotals();
    const retirementDistributions = calculateRetirementDistributions(
      householdTotals,
      [],
      activeTab,
    );
    const finalTaxTotals = calculateTaxTotals(
      householdTotals,
      retirementDistributions,
    );
    const { assetBalances } = calculateAssetBalances(
      householdTotals,
      retirementDistributions,
      finalTaxTotals,
    );

    // Calculate liability balances
    const liabilityBalances = calculateLiabilityBalances(formData.debts);
    const netWorthValues = calculateNetWorth(assetBalances, liabilityBalances);

    logger.info("Rendering details with:", {
      householdTotals,
      retirementDistributions,
      finalTaxTotals,
      assetBalances,
      liabilityBalances,
      netWorthValues,
    });

    return (
      <div>
        <BaseViewChart
          assetBalances={assetBalances}
          taxTotals={finalTaxTotals}
          currentYear={currentYear}
          yearsToProject={yearsToProject}
        />
        <div className="overflow-x-auto mt-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500">
                  Category
                </th>
                {Array.from({ length: yearsToProject }, (_, i) => (
                  <th
                    key={`year-${currentYear + i}`}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-500"
                  >
                    {currentYear + i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
              {/* Income Sources */}
              {/* First Member's Income Sources */}
              {formData.household.firstMember.incomeSources.map(
                (source, sourceIndex) => {
                  const projections = calculateIncomeSourceProjection(
                    source,
                    formData.household.firstMember.dateOfBirth,
                    formData.assumptions.lifeExpectancy.firstMember,
                  );
                  const displayName =
                    source.type === "Other" && source.customType
                      ? source.customType
                      : formatIncomeType(source.type);
                  const memberName = formData.household.firstMember.name;

                  return (
                    <React.Fragment key={`first-source-${sourceIndex}`}>
                      <tr>
                        <td className="px-4 py-2">
                          {`${getPossessiveForm(memberName)} ${displayName}`}
                        </td>
                        {projections.map((proj, yearIndex) => (
                          <td
                            key={`gross-${sourceIndex}-${yearIndex}`}
                            className="px-4 py-2"
                          >
                            {formatCurrency(proj.gross)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-2 pl-8">Deductions</td>
                        {projections.map((proj, yearIndex) => (
                          <td
                            key={`deductions-${sourceIndex}-${yearIndex}`}
                            className="px-4 py-2"
                          >
                            {formatCurrency(proj.deductions)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-2 pl-8">Net</td>
                        {projections.map((proj, yearIndex) => (
                          <td
                            key={`net-${sourceIndex}-${yearIndex}`}
                            className="px-4 py-2"
                          >
                            {formatCurrency(proj.net)}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                },
              )}

              {/* Spouse's Income Sources */}
              {formData.household.isMarried &&
                formData.household.secondMember.incomeSources.map(
                  (source, sourceIndex) => {
                    const projections = calculateIncomeSourceProjection(
                      source,
                      formData.household.secondMember.dateOfBirth,
                      formData.assumptions.lifeExpectancy.secondMember,
                    );
                    const displayName =
                      source.type === "Other" && source.customType
                        ? source.customType
                        : formatIncomeType(source.type);
                    const memberName = formData.household.secondMember.name;

                    return (
                      <React.Fragment key={`second-source-${sourceIndex}`}>
                        <tr>
                          <td className="px-4 py-2">
                            {`${getPossessiveForm(memberName)} ${displayName}`}
                          </td>
                          {projections.map((proj, yearIndex) => (
                            <td
                              key={`gross-${sourceIndex}-${yearIndex}`}
                              className="px-4 py-2"
                            >
                              {formatCurrency(proj.gross)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-4 py-2 pl-8">Deductions</td>
                          {projections.map((proj, yearIndex) => (
                            <td
                              key={`deductions-${sourceIndex}-${yearIndex}`}
                              className="px-4 py-2"
                            >
                              {formatCurrency(proj.deductions)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-4 py-2 pl-8">Net</td>
                          {projections.map((proj, yearIndex) => (
                            <td
                              key={`net-${sourceIndex}-${yearIndex}`}
                              className="px-4 py-2"
                            >
                              {formatCurrency(proj.net)}
                            </td>
                          ))}
                        </tr>
                      </React.Fragment>
                    );
                  },
                )}

              {/* Household Totals */}
              <tr className="bg-gray-50 font-medium">
                <td className="px-4 py-2">Total Household Net</td>
                {householdTotals.map((total, year) => (
                  <td key={`net-${year}`} className="px-4 py-2">
                    {formatCurrency(total.net)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Total Household Net Goal</td>
                {householdTotals.map((total, year) => (
                  <td key={`goal-${year}`} className="px-4 py-2">
                    {formatCurrency(total.goal)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Static Deficit/Surplus</td>
                {householdTotals.map((total, year) => (
                  <td key={`static-deficit-${year}`} className="px-4 py-2">
                    {formatCurrency(total.net - total.goal)}
                  </td>
                ))}
              </tr>

              {/* Retirement Distributions (if needed) */}
              <tr>
                <td className="px-4 py-2">Required Minimum Distributions</td>
                {retirementDistributions.map((dist, index) => (
                  <td key={`rmd-${index}`} className="px-4 py-2">
                    {formatCurrency(dist.rmdAmount || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Gross Retirement Distributions</td>
                {retirementDistributions.map((dist, index) => (
                  <td key={`dist-${index}`} className="px-4 py-2">
                    {formatCurrency(dist.gross || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Net Distribution</td>
                {retirementDistributions.map((dist, index) => (
                  <td key={`net-${index}`} className="px-4 py-2">
                    {formatCurrency(dist.net || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Total Deficit/Surplus</td>
                {householdTotals.map((total, year) => (
                  <td key={`final-deficit-${year}`} className="px-4 py-2">
                    {formatCurrency(
                      total.net +
                        retirementDistributions[year].net -
                        total.goal,
                    )}
                  </td>
                ))}
              </tr>

              {/* Tax Summary */}
              <tr className="bg-gray-50">
                <td className="px-4 py-2">Total Gross Household</td>
                {finalTaxTotals.map((tax, index) => (
                  <td key={`gross-${index}`} className="px-4 py-2">
                    {formatCurrency(tax.totalGrossIncome || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Total Taxes Withheld</td>
                {finalTaxTotals.map((tax, index) => (
                  <td key={`withheld-${index}`} className="px-4 py-2">
                    {formatCurrency(tax.taxesWithheld || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Other Deductions Withheld</td>
                {finalTaxTotals.map((tax, index) => (
                  <td key={`other-deductions-${index}`} className="px-4 py-2">
                    {formatCurrency(tax.otherDeductions || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">Projected Total Taxes Owed</td>
                {finalTaxTotals.map((tax, index) => (
                  <td key={`owed-${index}`} className="px-4 py-2">
                    {formatCurrency(tax.taxOwed || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2">
                  Surplus or Deficit Taxes Projected
                </td>
                {finalTaxTotals.map((tax, index) => {
                  const difference = tax.taxesWithheld - tax.taxOwed;
                  return (
                    <td
                      key={`tax-difference-${index}`}
                      className={`px-4 py-2 ${difference < 0 ? "text-red-600" : "text-green-600"}`}
                    >
                      {formatCurrency(difference)}
                    </td>
                  );
                })}
              </tr>

              {/* Asset Balances */}
              <tr className="bg-gray-50 font-medium">
                <td className="px-4 py-2">Asset Balances</td>
              </tr>
              <tr>
                <td className="px-4 py-2 pl-8">Non-Qualified Assets</td>
                {assetBalances.map((balance, index) => {
                  logger.info(
                    `Rendering Non-Qualified balance for year ${index}:`,
                    balance.nonQualified,
                  );
                  return (
                    <td key={index} className="px-4 py-2">
                      {formatCurrency(balance.nonQualified)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className="px-4 py-2 pl-8">Tax-Deferred Assets</td>
                {assetBalances.map((balance, index) => (
                  <td key={index} className="px-4 py-2">
                    {formatCurrency(balance.taxDeferred)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2 pl-8">Roth Assets</td>
                {assetBalances.map((balance, index) => (
                  <td key={index} className="px-4 py-2">
                    {formatCurrency(balance.roth)}
                  </td>
                ))}
              </tr>

              {/* Liabilities */}
              <tr className="bg-gray-50 font-medium">
                <td className="px-4 py-2">Liabilities</td>
              </tr>
              {formData.debts.map((debt, debtIndex) => (
                <tr key={`debt-${debt.name}`}>
                  <td className="px-4 py-2 pl-8">{debt.name}</td>
                  {liabilityBalances.map((yearBalances, year) => (
                    <td key={`debt-${debt.name}-${year}`} className="px-4 py-2">
                      {formatCurrency(yearBalances[debtIndex].balance)}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Net Worth */}
              <tr className="font-medium">
                <td className="px-4 py-2">Net Worth</td>
                {netWorthValues.map((netWorth, year) => (
                  <td key={`net-worth-${year}`} className="px-4 py-2">
                    {formatCurrency(netWorth)}
                  </td>
                ))}
              </tr>

              {/* Distribution Sources */}
              <tr>
                <td className="px-4 py-2">Distribution Sources</td>
                {retirementDistributions.map((dist, index) => (
                  <td key={`sources-${index}`} className="px-4 py-2">
                    <div>TD: {formatCurrency(dist.sources.taxDeferred)}</div>
                    <div>NQ: {formatCurrency(dist.sources.nonQualified)}</div>
                    <div>Roth: {formatCurrency(dist.sources.roth)}</div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderComparisonTab = () => (
    <div className="space-y-6">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Metric
              </th>
              {Object.keys(results).map(
                (scenario) =>
                  results[scenario] && ( // Only show column if data exists
                    <th
                      key={scenario}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider"
                    >
                      {scenario.replace(/([A-Z])/g, " $1").trim()}
                    </th>
                  ),
              )}
            </tr>
          </thead>
          <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-4 whitespace-nowrap font-medium">
                Total Retirement Income
              </td>
              {Object.values(results).map(
                (data, index) =>
                  data && ( // Only show cell if data exists
                    <td key={index} className="px-6 py-4 whitespace-nowrap">
                      {formatCurrency(data.totalRetirementIncome || 0)}
                    </td>
                  ),
              )}
            </tr>
            <tr>
              <td className="px-6 py-4 whitespace-nowrap font-medium">
                Total Taxes Paid
              </td>
              {Object.values(results).map(
                (data, index) =>
                  data && ( // Only show cell if data exists
                    <td key={index} className="px-6 py-4 whitespace-nowrap">
                      {formatCurrency(data.totalTaxesPaid || 0)}
                    </td>
                  ),
              )}
            </tr>
            <tr>
              <td className="px-6 py-4 whitespace-nowrap font-medium">
                Ending Balance
              </td>
              {Object.values(results).map(
                (data, index) =>
                  data && ( // Only show cell if data exists
                    <td key={index} className="px-6 py-4 whitespace-nowrap">
                      {formatCurrency(data.endingBalance || 0)}
                    </td>
                  ),
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const openDetailedView = () => {
    // Create a new window with the detailed view
    const detailedWindow = window.open("", "_blank");

    // Create the content for the new window
    const content = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Detailed Results</title>
                    <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
                </head>
                <body class="p-8">
                    <div id="detailed-content">
                        ${document.getElementById("detailed-view-content").outerHTML}
                    </div>
                </body>
            </html>
        `;

    detailedWindow.document.write(content);
    detailedWindow.document.close();
  };

  return (
    <div className="border rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-6">Calculation Results</h2>

      {/* Tab Navigation */}
      <div className="flex space-x-4 mb-6">
        <button
          className={`px-4 py-2 rounded ${activeTab === "summary" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          onClick={() => setActiveTab("summary")}
        >
          Summary
        </button>
        <button
          className={`px-4 py-2 rounded ${activeTab === "base" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          onClick={() => setActiveTab("base")}
        >
          Base View
        </button>
        <button
          className={`px-4 py-2 rounded ${activeTab === "ira" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          onClick={() => setActiveTab("ira")}
        >
          IRA View
        </button>
        <button
          className={`px-4 py-2 rounded ${activeTab === "roth" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          onClick={() => setActiveTab("roth")}
        >
          Roth IRA Conversion View
        </button>
        {(activeTab === "base" ||
          activeTab === "ira" ||
          activeTab === "roth") && (
          <button
            className="px-4 py-2 rounded bg-green-500 text-white hover:bg-green-600 flex items-center"
            onClick={openDetailedView}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            Open in New Window
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === "summary" && renderSummaryTab()}
        {activeTab === "base" && (
          <div id="detailed-view-content">
            {renderDetailsTab(results.baseScenario)}
          </div>
        )}
        {activeTab === "ira" && (
          <div id="detailed-view-content">
            {renderDetailsTab(results.iraDistributionScenario)}
          </div>
        )}
        {activeTab === "roth" && (
          <div id="detailed-view-content">
            {renderDetailsTab(results.rothConversionScenario)}
          </div>
        )}
        {activeTab === "comparison" && renderComparisonTab()}
      </div>
    </div>
  );
};

export default ResultsDisplay;
